import {
  ClockIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import type { Company, PrimaryContact } from "@type/contact";
import Image from "next/image";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import type { ComponentType, ReactNode, SVGProps } from "react";

import { mapsDirectionsUrl, telHref } from "./contact-utils";

interface ContactHeroProps {
  company: Company;
  primaryContact: PrimaryContact;
}

export const ContactHero = ({ company, primaryContact }: ContactHeroProps) => {
  const { t } = useTranslation("contact");
  const [mainPhone, ...otherPhones] = primaryContact.phones;
  const { address, openingHours } = company;
  const isWeekendClosed = !(openingHours.saturday || openingHours.sunday);

  return (
    <>
      {/* Hero wchodzi pod stały header (offset z layout/layout.tsx), aby nie było
          prześwitu - wysokość headera zależy od języka i szerokości ekranu */}
      <section
        aria-labelledby="contact-hero-title"
        className="relative isolate -mt-[76px] overflow-hidden bg-[#051124] pt-[76px] text-white md:-mt-[92px] md:pt-[92px] lg:-mt-[108px] lg:pt-[108px]"
      >
        <Image
          alt=""
          className="-z-20 object-cover object-[70%_center]"
          fill
          priority
          sizes="100vw"
          src="/img/contact.webp"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-linear-to-r from-[#051124] via-[#051124]/90 to-[#051124]/40 md:via-[#051124]/80 md:to-[#051124]/20"
        />

        <div className="mx-auto max-w-7xl px-4 pt-6 pb-28 sm:px-6 md:pt-10 md:pb-36 lg:px-8">
          <nav aria-label={t("breadcrumb.label")}>
            <ol className="flex items-center gap-2 text-white/60 text-xs">
              <li>
                <Link className="transition hover:text-white" href="/">
                  {t("breadcrumb.home")}
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-[#c1a166]">
                {t("breadcrumb.current")}
              </li>
            </ol>
          </nav>

          <h1
            className="mt-6 max-w-4xl text-balance font-bold text-4xl leading-[1.1] tracking-tight sm:text-5xl lg:text-[3.5rem]"
            id="contact-hero-title"
          >
            {t("hero.titleLead")}{" "}
            <span className="text-[#c1a166]">{t("hero.titleHighlight")}</span>
          </h1>
          <p className="mt-5 max-w-xl text-base text-white/75 leading-relaxed md:text-lg">
            {t("hero.description")}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a
              className="inline-flex items-center justify-center gap-2.5 rounded-md bg-[#c1a166] px-5 py-3 font-semibold text-[#051124] text-sm shadow-lg transition hover:bg-[#d4b77a] focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
              href={telHref(mainPhone)}
            >
              <PhoneIcon aria-hidden="true" className="size-4" />
              {mainPhone}
            </a>
            <a
              className="inline-flex items-center justify-center gap-2.5 rounded-md border border-white/30 px-5 py-3 font-semibold text-sm text-white transition hover:border-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
              href={`mailto:${primaryContact.email}`}
            >
              <EnvelopeIcon aria-hidden="true" className="size-4" />
              <span className="break-all">{primaryContact.email}</span>
            </a>
            <a
              className="inline-flex items-center justify-center gap-2.5 rounded-md border border-white/30 px-5 py-3 font-semibold text-sm text-white transition hover:border-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
              href={mapsDirectionsUrl(company.mapsQuery)}
              rel="noopener noreferrer"
              target="_blank"
            >
              <MapPinIcon aria-hidden="true" className="size-4" />
              {t("hero.directions")}
            </a>
          </div>
        </div>
      </section>

      {/* Pasek z najważniejszymi danymi - nachodzi na hero */}
      <div className="relative z-10 mx-auto -mt-16 max-w-7xl px-4 sm:px-6 md:-mt-20 lg:px-8">
        <dl className="grid overflow-hidden rounded-xl bg-white shadow-[0_20px_50px_-20px_rgba(5,17,36,0.35)] ring-1 ring-black/5 sm:grid-cols-2 xl:grid-cols-4">
          <QuickInfoItem icon={MapPinIcon} label={t("quickInfo.headquarters")}>
            <span className="block font-semibold text-[#051124]">
              {address.street}
            </span>
            <span className="block text-gray-500 text-sm">
              {address.postalCode} {address.city}
            </span>
          </QuickInfoItem>

          <QuickInfoItem icon={ClockIcon} label={t("quickInfo.hours")}>
            <span className="block font-semibold text-[#051124]">
              {openingHours.weekdays
                ? t("hours.weekdaysShort", { hours: openingHours.weekdays })
                : t("hours.closed")}
            </span>
            {isWeekendClosed && (
              <span className="block text-gray-500 text-sm">
                {t("hours.weekendClosed")}
              </span>
            )}
          </QuickInfoItem>

          <QuickInfoItem icon={PhoneIcon} label={primaryContact.label}>
            <a
              className="block font-semibold text-[#051124] hover:text-[#8a6d2f]"
              href={telHref(mainPhone)}
            >
              {mainPhone}
            </a>
            {otherPhones.map((phone) => (
              <a
                className="block text-gray-500 text-sm hover:text-[#8a6d2f]"
                href={telHref(phone)}
                key={phone}
              >
                {phone}
              </a>
            ))}
          </QuickInfoItem>

          <QuickInfoItem icon={EnvelopeIcon} label={t("quickInfo.email")}>
            <a
              className="block break-all font-semibold text-[#051124] hover:text-[#8a6d2f]"
              href={`mailto:${primaryContact.email}`}
            >
              {primaryContact.email}
            </a>
            {primaryContact.emailNote && (
              <span className="block text-gray-500 text-sm">
                {primaryContact.emailNote}
              </span>
            )}
          </QuickInfoItem>
        </dl>
      </div>
    </>
  );
};

interface QuickInfoItemProps {
  children: ReactNode;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
}

const QuickInfoItem = ({ children, icon: Icon, label }: QuickInfoItemProps) => (
  <div className="flex gap-4 border-gray-100 border-b p-5 last:border-b-0 sm:nth-[n+3]:border-b-0 sm:odd:border-r xl:border-r xl:border-b-0 xl:p-6 xl:last:border-r-0">
    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#c1a166]/15 text-[#8a6d2f]">
      <Icon aria-hidden="true" className="size-5" />
    </div>
    <div className="min-w-0">
      <dt className="font-semibold text-[11px] text-gray-500 uppercase tracking-[0.14em]">
        {label}
      </dt>
      <dd className="mt-1">{children}</dd>
    </div>
  </div>
);
